import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import config from "../config";
import { toast } from "react-toastify";
import "../components/landing/landing.css";
import { LandingComingSoon } from "../components/landing/LandingComingSoon";

import { LandingNavbar }         from "../components/landing/LandingNavbar";
import { LandingHero }           from "../components/landing/LandingHero";
import { LandingTrustSection }   from "../components/landing/LandingTrustSection";
import { LandingDeviceShowcase } from "../components/landing/LandingDeviceShowcase";
import { LandingMapSection }     from "../components/landing/LandingMapSection";
import { LandingBusinessCTA }    from "../components/landing/LandingBusinessCTA";
import { LandingAppDownload }    from "../components/landing/LandingAppDownload";
import { LandingAboutSection }   from "../components/landing/LandingAboutSection";
import { LandingFAQSection }     from "../components/landing/LandingFAQSection";
import { LandingPageFooter }     from "../components/landing/LandingPageFooter";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600;1,9..144,600&family=Outfit:wght@300;400;500&display=swap";

const APP_TYPE = process.env.REACT_APP_APP_TYPE || "dev";

function getAuthRedirect(user) {
  if (APP_TYPE === "admin") {
    const has = (p) => user.permissions && user.permissions.includes(p);
    if (user.role === "admin" || has("admin_access")) return "/control-tower";
    if (has("content")) return "/categories";
    if (has("users")) return "/user-management";
    if (has("venues")) return "/venue-verification";
    return `/view-profile/${user.id}`;
  }
  if (APP_TYPE === "business") return "/applications";

  // dev / default
  if (user.role === "admin" || (user.permissions && user.permissions.includes("admin_access"))) {
    return "/control-tower";
  }
  if (user.permissions && user.permissions.includes("dashboard")) return "/index-1";
  return "/applications";
}

export default function LandingPage() {
  const { user, isAuthenticated, auth } = useAuth();
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get("preview") === "true";
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [theme, setTheme] = useState("light");

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  // Fetch settings to check if Coming Soon / Maintenance mode is active
  useEffect(() => {
    const fetchSettings = () => {
      fetch(`${config.API_BASE_URL}/api/settings`)
        .then((res) => res.json())
        .then((data) => {
          if (data.maintenance_mode !== undefined) {
            setMaintenanceMode(!!data.maintenance_mode);
          }
        })
        .catch((err) => console.error("Failed to fetch settings in LandingPage:", err))
        .finally(() => setLoadingSettings(false));
    };

    fetchSettings();
    window.addEventListener("settingsChanged", fetchSettings);
    return () => window.removeEventListener("settingsChanged", fetchSettings);
  }, []);

  const handleSwitchToLive = async () => {
    try {
      const res = await auth.fetch(`${config.API_BASE_URL}/api/settings/maintenance`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ maintenance_mode: false }),
      });
      if (!res.ok) throw new Error("Failed to switch to live");
      setMaintenanceMode(false);
      window.dispatchEvent(new Event("settingsChanged"));
      toast.success("Landing page is now LIVE!");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to switch to live");
    }
  };

  // Monitor scroll for sticky bar visibility
  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar after scrolling down 500px
      setShowStickyBar(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Inject Google Fonts and SEO attributes on mount
  useEffect(() => {
    // 1. Google Fonts
    const existingFonts = document.getElementById("hs-landing-fonts");
    if (!existingFonts) {
      const link = document.createElement("link");
      link.id   = "hs-landing-fonts";
      link.rel  = "stylesheet";
      link.href = FONTS_HREF;
      document.head.appendChild(link);
    }

    // 2. SEO Title
    const originalTitle = document.title;
    document.title = "HubScroll - Discover the UAE's Best Venues";

    // 3. SEO Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    let originalDesc = metaDesc ? metaDesc.getAttribute("content") : "";
    
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      "HubScroll is the easiest way to discover the most exciting cafés, restaurants, lounges, and nightlife venues across Dubai and the UAE."
    );

    // Clean up on unmount
    return () => {
      const el = document.getElementById("hs-landing-fonts");
      if (el) el.remove();
      
      document.title = originalTitle;
      if (metaDesc) {
        if (originalDesc) {
          metaDesc.setAttribute("content", originalDesc);
        } else {
          metaDesc.remove();
        }
      }
    };
  }, []);

  // Authenticated users skip the landing page
  if (isAuthenticated && user && !isPreview) {
    return <Navigate to={getAuthRedirect(user)} replace />;
  }

  // If accessing the admin portal, unauthenticated users must go to sign-in directly
  const isAdminPortal =
    APP_TYPE === "admin" ||
    (typeof window !== "undefined" &&
      (window.location.hostname.startsWith("admin.") || window.location.hostname.includes("admin")));

  if (isAdminPortal) {
    return <Navigate to="/sign-in" replace />;
  }

  // If maintenance / coming soon mode is active and not previewing live
  if (maintenanceMode && !isPreview && !loadingSettings) {
    return (
      <LandingComingSoon
        theme={theme}
        toggleTheme={toggleTheme}
        user={user}
        onSwitchToLive={user && (user.role === "admin" || (user.permissions && user.permissions.includes("admin_access"))) ? handleSwitchToLive : null}
        onPreviewLive={() => {
          const url = new URL(window.location.href);
          url.searchParams.set("preview", "true");
          window.location.href = url.toString();
        }}
      />
    );
  }

  return (
    <div className={`hs-landing ${theme === "light" ? "lp-light-theme" : ""}`}>
      {/* Admin Live Preview Banner if Coming Soon mode is active */}
      {maintenanceMode && isPreview && (
        <div style={{
          background: "linear-gradient(90deg, #d97706, #b45309)",
          color: "#fff",
          padding: "0.6rem 1.25rem",
          fontSize: "0.85rem",
          position: "sticky",
          top: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "0.75rem",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          fontFamily: "'Outfit', sans-serif"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "1rem" }}>👁️</span>
            <span><strong>Live Preview:</strong> You are previewing the Live Landing Page. Regular visitors see the <em>Coming Soon</em> page.</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {user && (user.role === "admin" || (user.permissions && user.permissions.includes("admin_access"))) && (
              <button
                onClick={handleSwitchToLive}
                style={{
                  background: "#10b981",
                  color: "#fff",
                  border: "none",
                  borderRadius: "9999px",
                  padding: "0.3rem 0.85rem",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Publish Live to Public
              </button>
            )}
            <button
              onClick={() => {
                const url = new URL(window.location.href);
                url.searchParams.delete("preview");
                window.location.href = url.pathname;
              }}
              style={{
                background: "rgba(0,0,0,0.4)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "9999px",
                padding: "0.3rem 0.85rem",
                fontSize: "0.78rem",
                cursor: "pointer"
              }}
            >
              Exit Preview &rarr;
            </button>
          </div>
        </div>
      )}
      {/* 1. Navigation Bar */}
      <LandingNavbar theme={theme} toggleTheme={toggleTheme} />
      
      {/* 2. Hero Section */}
      <LandingHero />
      
      {/* 3. Trust / Social Proof */}
      <LandingTrustSection />
      
      {/* 4 & 5. Discovery Experience & Key Features */}
      <LandingDeviceShowcase />
      
      {/* 6. UAE Focus Map */}
      <LandingMapSection />
      
      {/* 7. Business Section */}
      <LandingBusinessCTA />
      
      {/* 8. App Download Section */}
      <LandingAppDownload />
      
      {/* 9. About HubScroll */}
      <LandingAboutSection />
      
      {/* 10. FAQ Section */}
      <LandingFAQSection />
      
      {/* 11. Footer */}
      <LandingPageFooter />

      {/* Extra: Sticky Mobile Download Bar */}
      <div className={`lp-sticky-bar ${showStickyBar ? "lp-active" : ""}`}>
        <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "0.95rem" }} className="lp-text-dark">
          hub<span className="lp-text-blue">scroll</span>
        </span>
        <div className="lp-flex lp-gap-2">
          <a 
            href="#download"
            className="lp-text-xs lp-px-3 lp-py-15 lp-r-full lp-bg-white lp-text-dark lp-font-medium lp-border"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            iOS
          </a>
          <a 
            href="#download"
            className="lp-text-xs lp-px-3 lp-py-15 lp-r-full lp-border lp-bc-b15 lp-text-dark"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Android
          </a>
        </div>
      </div>

      {/* Extra: Floating Business Button */}
      <Link to="/sign-in" className="lp-floating-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
        List Your Venue
      </Link>
    </div>
  );
}
